import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { Facebook as FacebookIcon } from '@mui/icons-material';

// Define the API endpoint
const API_ENDPOINT = import.meta.env.VITE_API_ENDPOINT || '';

interface FacebookPage {
  id: string;
  name: string;
}

interface FacebookIntegrationProps {
  onPageSelect?: (page: FacebookPage) => void;
}

const FacebookIntegration: React.FC<FacebookIntegrationProps> = ({ onPageSelect }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [authenticated, setAuthenticated] = useState<boolean>(false);
  const [pages, setPages] = useState<FacebookPage[]>([]);
  const [selectedPage, setSelectedPage] = useState<FacebookPage | null>(null);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);

  // Check if user is authenticated with Facebook
  useEffect(() => {
    checkAuthentication();
  }, []);

  const checkAuthentication = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get the current authenticated session
      const { fetchAuthSession } = await import('aws-amplify/auth');
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        console.error('No authentication token available');
        setError('Authentication error. Please sign in again.');
        return;
      }

      // Get Facebook pages to check if authenticated
      const response = await fetch(`${API_ENDPOINT}/facebook/pages`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': idToken
        }
      });

      if (response.status === 401) {
        // User is not authenticated with Facebook
        setAuthenticated(false);
        setPages([]);
      } else if (response.ok) {
        // User is authenticated with Facebook
        const data = await response.json();
        setAuthenticated(true);
        setPages(data.pages || []);
      } else {
        // Error occurred
        const errorText = await response.text();
        console.error(`HTTP error! status: ${response.status}`, errorText);
        setError(`Error checking Facebook authentication: ${errorText}`);
      }
    } catch (error) {
      console.error('Error checking Facebook authentication:', error);
      setError(`Error checking Facebook authentication: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAuthenticate = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get the current authenticated session
      const { fetchAuthSession } = await import('aws-amplify/auth');
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        console.error('No authentication token available');
        setError('Authentication error. Please sign in again.');
        return;
      }

      // Get the redirect URI
      const redirectUri = window.location.origin + '/facebook-callback';

      // Get Facebook authentication URL
      const response = await fetch(`${API_ENDPOINT}/facebook/auth?redirect_uri=${encodeURIComponent(redirectUri)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': idToken
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`HTTP error! status: ${response.status}`, errorText);
        throw new Error(`Error getting Facebook authentication URL: ${errorText}`);
      }

      const data = await response.json();
      
      // Open Facebook authentication URL in a new window
      const authWindow = window.open(data.authUrl, 'facebook-auth', 'width=600,height=600');
      
      // Poll for authentication completion
      const checkAuthInterval = setInterval(async () => {
        if (authWindow && authWindow.closed) {
          clearInterval(checkAuthInterval);
          
          // Check if authentication was successful
          await checkAuthentication();
        }
      }, 1000);
    } catch (error) {
      console.error('Error authenticating with Facebook:', error);
      setError(`Error authenticating with Facebook: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handlePageSelect = (page: FacebookPage) => {
    setSelectedPage(page);
    if (onPageSelect) {
      onPageSelect(page);
    }
    setDialogOpen(false);
  };

  const openPageSelector = () => {
    setDialogOpen(true);
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Facebook Integration
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CircularProgress size={24} sx={{ mr: 1 }} />
            <Typography>Loading...</Typography>
          </Box>
        ) : authenticated ? (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              Connected to Facebook
            </Alert>
            
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography variant="body1" sx={{ mr: 2 }}>
                {selectedPage ? `Selected Page: ${selectedPage.name}` : 'No page selected'}
              </Typography>
              <Button 
                variant="outlined" 
                color="primary" 
                onClick={openPageSelector}
                disabled={pages.length === 0}
              >
                {selectedPage ? 'Change Page' : 'Select Page'}
              </Button>
            </Box>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
              <DialogTitle>Select Facebook Page</DialogTitle>
              <DialogContent>
                {pages.length === 0 ? (
                  <Typography>No Facebook pages found. Make sure you have admin access to at least one Facebook page.</Typography>
                ) : (
                  <List>
                    {pages.map((page, index) => (
                      <React.Fragment key={page.id}>
                        <ListItem button onClick={() => handlePageSelect(page)}>
                          <ListItemAvatar>
                            <Avatar>
                              <FacebookIcon />
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText primary={page.name} />
                        </ListItem>
                        {index < pages.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
              </DialogActions>
            </Dialog>
          </Box>
        ) : (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              Connect your Facebook account to manage posts on your Facebook pages.
            </Alert>
            <Button
              variant="contained"
              color="primary"
              startIcon={<FacebookIcon />}
              onClick={handleAuthenticate}
              disabled={loading}
            >
              Connect to Facebook
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default FacebookIntegration;
